import {
  Stack,
  Group,
  NumberInput,
  Button,
  Text,
  ColorInput,
  Select,
  Alert,
  TextInput,
} from "@mantine/core";
import { IconArrowLeft, IconInfoCircle, IconTrash } from "@tabler/icons-react";
import { FIELD_ELEMENTS, elementDisplayName, toMm } from "./badgeConstants";

// Sidebar shown when an element is selected: its type-specific properties,
// layer assignment, and delete action.
export default function BadgeElementEditor({
  element,
  size,
  updateElement,
  removeElement,
  onBack,
}) {
  const isTextLike = element.type === "text" || element.type === "field";

  const widthMm = toMm(size.width || 1, size.unit);
  const heightMm = toMm(size.height || 1, size.unit);

  const pctToMm = (pct, totalMm) => Number(((pct / 100) * totalMm).toFixed(1));

  const updateFromMm = (key, mmValue, totalMm, maxPct) => {
    const pct = ((Number(mmValue) || 0) / totalMm) * 100;
    updateElement(element.id, key, Math.min(Math.max(pct, 0), maxPct));
  };

  return (
    <Stack
      gap="xs"
      p="sm"
      style={{
        border: "1px solid #e9ecef",
        borderRadius: "8px",
        backgroundColor: "#f8f9fa",
      }}
    >
      <Button
        variant="light"
        fullWidth
        justify="flex-start"
        leftSection={<IconArrowLeft size={18} />}
        onClick={onBack}
      >
        Back
      </Button>

      <Text size="sm" fw={700} c="blue">
        Edit {elementDisplayName(element)}
      </Text>

      {element.type === "text" && (
        <TextInput
          label="Text"
          value={element.text || ""}
          onChange={(e) =>
            updateElement(element.id, "text", e.currentTarget.value)
          }
        />
      )}

      {element.type === "field" && (
        <Select
          label="Field"
          value={element.fieldKey}
          onChange={(val) => updateElement(element.id, "fieldKey", val)}
          data={FIELD_ELEMENTS.map((f) => ({
            value: f.fieldKey,
            label: f.label,
          }))}
        />
      )}

      <Text size="xs" fw={600} c="dimmed" mt="xs">
        Position &amp; Size (mm)
      </Text>

      <Group grow>
        <NumberInput
          label="From left"
          value={pctToMm(element.xPct, widthMm)}
          onChange={(val) =>
            updateFromMm("xPct", val, widthMm, 100 - element.widthPct)
          }
          min={0}
          max={widthMm}
          step={0.5}
          decimalScale={1}
        />
        <NumberInput
          label="From top"
          value={pctToMm(element.yPct, heightMm)}
          onChange={(val) =>
            updateFromMm("yPct", val, heightMm, 100 - element.heightPct)
          }
          min={0}
          max={heightMm}
          step={0.5}
          decimalScale={1}
        />
      </Group>

      <Group grow>
        <NumberInput
          label="Width"
          value={pctToMm(element.widthPct, widthMm)}
          onChange={(val) =>
            updateFromMm("widthPct", val, widthMm, 100 - element.xPct)
          }
          min={0}
          max={widthMm}
          step={0.5}
          decimalScale={1}
        />
        <NumberInput
          label="Height"
          value={pctToMm(element.heightPct, heightMm)}
          onChange={(val) =>
            updateFromMm("heightPct", val, heightMm, 100 - element.yPct)
          }
          min={0}
          max={heightMm}
          step={0.5}
          decimalScale={1}
        />
      </Group>

      {isTextLike && (
        <>
          <Group grow>
            <NumberInput
              label="Font Size (px)"
              value={element.fontSize}
              min={1}
              onChange={(val) =>
                updateElement(element.id, "fontSize", val === "" ? "" : val)
              }
            />
            <ColorInput
              label="Color"
              value={element.color}
              onChange={(val) => updateElement(element.id, "color", val)}
            />
          </Group>
          <Select
            label="Text Alignment"
            data={["left", "center", "right"]}
            value={element.align}
            onChange={(val) => updateElement(element.id, "align", val)}
          />
        </>
      )}

      {element.type === "rectangle" && (
        <ColorInput
          label="Fill"
          value={element.fill}
          onChange={(val) => updateElement(element.id, "fill", val)}
        />
      )}

      {element.type === "qr" && (
        <Alert
          variant="light"
          color="blue"
          icon={<IconInfoCircle size={16} />}
          p="xs"
        >
          <Text size="xs">
            The QR code is a placeholder here. Each attendee&apos;s real code is
            generated when the badge is printed.
          </Text>
        </Alert>
      )}

      <Group grow mt="xs">
        <Button
          color="red"
          variant="light"
          leftSection={<IconTrash size={16} />}
          onClick={() => removeElement(element.id)}
        >
          Delete
        </Button>
      </Group>
    </Stack>
  );
}
